import apiClient from "../apiClient";
import { ENDPOINTS } from "../endpoints";

export const identityService = {
  // Current user
  getMe: async () => (await apiClient.get(ENDPOINTS.me)).data,

  // Employees -- listing reads HRMS's own directory (read-only, unaffected
  // by anything below); creation goes through the ATS-owned endpoint so it
  // has its own table/flow and never touches HRMS's employee router.
  listEmployees: async () => {
    const [{ data }, profiles] = await Promise.all([
      apiClient.get(ENDPOINTS.employees),
      identityService.listEmployeeProfiles().catch(() => []), // don't break the list if this 403s for a role that can't manage employees
    ]);
    const profileByEmployeeId = Object.fromEntries((profiles || []).map((p) => [p.employee_id, p]));
    return (data || []).map((e) => {
      const profile = profileByEmployeeId[e.id];
      return {
        ...e,
        name: [e.first_name, e.last_name].filter(Boolean).join(" ") || e.email,
        is_active: e.status ? e.status === "Active" : true,
        // ATS-captured profile data wins over whatever HRMS happens to have --
        // it's the source of truth for employees created through this app.
        pan_number: profile?.pan_number ?? e.pan_number,
        aadhar_number: profile?.aadhar_number ?? e.aadhar_number,
        address: profile?.address ?? e.address,
      };
    });
  },
  createEmployee: async (payload) => (await apiClient.post(ENDPOINTS.employeeCreate, payload)).data,

  // ATS-owned supplementary employee fields (PAN, Aadhaar, Address) --
  // saved via a separate call/table from the HRMS employee record itself.
  listEmployeeProfiles: async () => (await apiClient.get(ENDPOINTS.employeeProfiles)).data,
  saveEmployeeProfile: async (employeeId, payload) =>
    (await apiClient.post(ENDPOINTS.employeeProfile(employeeId), payload)).data,

  // Roles
  listPermissions: async () => (await apiClient.get(ENDPOINTS.permissions)).data,
  listRoles: async () => (await apiClient.get(ENDPOINTS.roles)).data,
  createRole: async (payload) => (await apiClient.post(ENDPOINTS.roles, payload)).data,
  updateRole: async (id, payload) => (await apiClient.patch(ENDPOINTS.role(id), payload)).data,
  deleteRole: async (id) => (await apiClient.delete(ENDPOINTS.role(id))).data,

  // User <-> role assignment
  listUserRoles: async () => (await apiClient.get(ENDPOINTS.userRoles)).data,
  assignRole: async (payload) => (await apiClient.post(ENDPOINTS.userRoles, payload)).data,
  revokeRole: async (userType, identityId) =>
    (await apiClient.delete(ENDPOINTS.userRole(userType, identityId))).data,

  // Customers / client portal contacts
  createCustomer: async (payload) => (await apiClient.post(ENDPOINTS.customers, payload)).data,
  createClientContact: async (customerId, payload) =>
    (await apiClient.post(ENDPOINTS.customerContacts(customerId), payload)).data,

  // Teams
  listTeams: async () => (await apiClient.get(ENDPOINTS.teams)).data,
  createTeam: async (payload) => (await apiClient.post(ENDPOINTS.teams, payload)).data,
  updateTeam: async (id, payload) => (await apiClient.patch(ENDPOINTS.team(id), payload)).data,
  deleteTeam: async (id) => (await apiClient.delete(ENDPOINTS.team(id))).data,
  addTeamMember: async (id, employeeId) =>
    (await apiClient.post(ENDPOINTS.teamMembers(id), { employee_id: employeeId })).data,
  removeTeamMember: async (id, employeeId) =>
    (await apiClient.delete(ENDPOINTS.teamMember(id, employeeId))).data,
};
